import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { AdminOverview } from '@platewell/schema';
import { adminOverview, signOut } from './lib/api.js';
import { useAdminAccess, useSession } from './lib/session.js';

/**
 * Chrome for the support console.
 *
 * It replaces the app sidebar rather than extending it: an agent working here is looking at
 * the service, not at one household, so collections, tags and the household switcher would
 * all be noise. It also deliberately does not wait on `usePlatewell`, so the console opens
 * for an admin with no household of their own and does not sit behind the CRDT boot.
 *
 * The permission check below decides what to *render*. It is not the security boundary —
 * every /api/admin route re-reads the permission from D1 on each request.
 */
export function AdminShell() {
  const { canAdmin, resolved } = useAdminAccess();
  const session = useSession();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [overview, setOverview] = useState<AdminOverview | null>(null);

  useEffect(() => {
    if (!canAdmin) return;
    let cancelled = false;
    void adminOverview()
      .then((value) => {
        if (!cancelled) setOverview(value);
      })
      // Counts and the environment pill are decoration; losing them must not blank the page.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [canAdmin]);

  if (!resolved) return <div className="boot">Checking access…</div>;

  if (!canAdmin) {
    return (
      <div className="boot">
        <div className="empty">
          <h2>Not available</h2>
          <Link className="btn" to="/">
            Back to your recipes
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="shell admin-shell">
      <aside className="sidebar admin-sidebar">
        <Link className="brand admin-brand" to="/">
          <img className="brand-mark" src="/logo.png" alt="" width={32} height={32} /> Platewell
          <span className="admin-pill">Admin</span>
        </Link>

        <nav className="nav">
          <span className="nav-heading">Admin</span>
          <AdminNavItem
            to="/admin/households"
            label="Households"
            active={pathname.startsWith('/admin/households')}
            count={overview?.counts.households}
          />
          <AdminNavItem
            to="/admin/users"
            label="Users"
            active={pathname.startsWith('/admin/users')}
            count={overview?.counts.users}
          />
        </nav>

        <span className="spacer" />
        {/* Same treatment as the app sidebar: a full reload, so the household manager, its
            sockets and every cached document are torn down rather than left holding a
            session that no longer exists. */}
        <button
          className="nav-item"
          onClick={() => {
            void signOut().then(() => window.location.reload());
          }}
        >
          Sign out
        </button>
      </aside>

      <div className="main">
        <header className="topbar admin-topbar">
          <span className="admin-topbar-label">Support console</span>
          <span className="spacer" />
          {overview != null && (
            <span className="admin-env">
              <span className="admin-env-dot" aria-hidden="true" />
              {overview.environment}
            </span>
          )}
          <span className="admin-agent">{session.user.email}</span>
        </header>

        <main className="content admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function AdminNavItem({
  to,
  label,
  active,
  count,
}: {
  to: '/admin/users' | '/admin/households';
  label: string;
  active: boolean;
  count: number | undefined;
}) {
  return (
    <Link className={`nav-item ${active ? 'active' : ''}`} to={to}>
      {label}
      {count != null && <span className="nav-count">{count}</span>}
    </Link>
  );
}
